import { ReactNode } from 'react';

import { Stack, Text } from '@mantine/core';

import { SubscriptionData, SubscriptionFromSubscriptionType, SubscriptionType } from 'models';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { SubscriptionUpdateCommonProps } from '../types';
import { ContactSales } from './ContactSales';
import { LegalCheckbox } from './LegalCheckbox';
import { PriceCalculation } from './PriceCalculation';
import { SubscriptionBillingCycleControl } from './SubscriptionBillingCycleControl';
import { SubscriptionUpdateState, SubscriptionUpdateStateController } from './useSubscriptionUpdate';

type SubscribeModalContentsProps<T extends SubscriptionType> = {
    type: T;
    toSubscription: SubscriptionFromSubscriptionType<T>;
    additionalSubscriptions?: SubscriptionData[];
    SubmitButton: ReactNode;
    quantity: number;
    isDowngrade?: boolean;
    children?: ReactNode;
} & SubscriptionUpdateCommonProps &
    Omit<SubscriptionUpdateState, 'loading'> &
    Omit<SubscriptionUpdateStateController, 'submitSubscriptionUpdate'>;

export const SubscribeModalContent = <T extends SubscriptionType>({
    type,
    toSubscription,
    quantity,
    additionalSubscriptions = [],
    isDowngrade,
    SubmitButton,
    children,
    billingCycle,
    setBillingCycle,
    legalChecked,
    setLegalChecked,
    setLegalError,
    legalError,
    team,
}: SubscribeModalContentsProps<T>) => {
    const isNewCompanyPremium =
        !team.subscriptionBillingCycle &&
        SubscriptionHelpers.includesPaidCompanyPremium([
            { subscription: toSubscription, quantity },
            ...additionalSubscriptions,
        ]);

    return (
        <>
            {isNewCompanyPremium ? (
                <Text>Premium plan is billed annually.</Text>
            ) : (
                <SubscriptionBillingCycleControl
                    currentBillingCycle={team.subscriptionBillingCycle}
                    toBillingCycle={billingCycle}
                    onChange={setBillingCycle}
                />
            )}

            {children}

            <PriceCalculation
                type={type}
                subscription={toSubscription}
                additionalSubscriptions={additionalSubscriptions}
                billingCycle={billingCycle}
                quantity={quantity}
                team={team}
            />

            <LegalCheckbox
                value={legalChecked}
                onChange={setLegalChecked}
                error={legalError}
                onReset={() => setLegalError(false)}
                isDowngrade={isDowngrade}
            />

            <Stack gap="xs">
                {SubmitButton}
                <ContactSales bg="gray.0" />
            </Stack>
        </>
    );
};
